import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import InviteMemberModal from '../../components/features/InviteMemberModal';
import SendOrganizationalCoin from '../../components/features/SendOrganizationalCoin';
import CoinItem from '../../components/common/CoinItem';
import RichTextEditor from '../../components/common/RichTextEditor';
import BackButton from '../../components/common/BackButton';
import { stripHTML } from '../../utils/htmlSanitizer';
import './OrganizationDetail.css';

function OrganizationDetail() {
  const auth = useAuth();
  const { orgId } = useParams(); // Supports both UUID and slug
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showAllMembersModal, setShowAllMembersModal] = useState(false);
  const [transactionsToShow, setTransactionsToShow] = useState(5);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState({});
  const [processingApproval, setProcessingApproval] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;
        const currentUserId = auth.user.profile.sub;

        // Fetch current user data and organization profile
        const [userData, orgData] = await Promise.all([
          apiService.getCurrentUser(idToken),
          apiService.getUserProfile(idToken, orgId)
        ]);

        setCurrentUser(userData);

        if (orgData.user_type !== 'organization') {
          setError('This is not an organization');
          setLoading(false);
          return;
        }

        setOrganization(orgData);

        // Check if current user is a member
        // Note: orgId might be a slug, so we need to compare against the actual user_id
        const userOrgs = await apiService.getUserOrganizations(idToken);
        const membership = userOrgs.find(org => org.organization_id === orgData.user_id);

        if (membership) {
          setIsMember(true);
          setIsAdmin(!!membership.is_admin);

          // Fetch additional data if user is a member
          const [membersData, transactionsData] = await Promise.all([
            apiService.getOrganizationMembers(idToken, orgId),
            apiService.getUserTransactionsById(idToken, orgId, {
              type: 'sent',
              limit: transactionsToShow
            })
          ]);

          setMembers(membersData.members || []);
          setTransactions(transactionsData.transactions || []);

          // Fetch pending requests if admin
          if (membership.is_admin) {
            const pendingData = await apiService.getPendingCoinRequests(idToken, orgId);
            setPendingRequests(pendingData.transactions || []);
          }
        }
      } catch (err) {
        console.error('Error fetching organization data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (orgId) {
      fetchOrganizationData();
    }
  }, [orgId, auth.isAuthenticated, auth.user, transactionsToShow]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleMessageEdit = (txId, newMessage) => {
    setEditingMessage(prev => ({
      ...prev,
      [txId]: newMessage
    }));
  };

  const handleApproveRequest = async (txId, originalDescription) => {
    const updatedMessage = editingMessage[txId] !== undefined ? editingMessage[txId] : originalDescription;

    // Validate that description is not empty (strip HTML first)
    const plainText = stripHTML(updatedMessage).trim();
    if (!plainText) {
      alert('Message cannot be empty. Please provide a description for the coin.');
      return;
    }

    setProcessingApproval(prev => ({ ...prev, [txId]: 'approving' }));

    try {
      const idToken = auth.user.id_token;

      // Approve the transaction and update coin description if edited
      const description = (editingMessage[txId] !== undefined && editingMessage[txId] !== originalDescription)
        ? editingMessage[txId]
        : undefined;
      await apiService.approveCoinRequest(idToken, txId, description);

      // Refresh pending requests
      const pendingData = await apiService.getPendingCoinRequests(idToken, orgId);
      setPendingRequests(pendingData.transactions || []);

      // Refresh transactions
      const transactionsData = await apiService.getUserTransactionsById(idToken, orgId, {
        type: 'sent',
        limit: transactionsToShow
      });
      setTransactions(transactionsData.transactions || []);

      // Clear editing state
      setEditingMessage(prev => {
        const newState = { ...prev };
        delete newState[txId];
        return newState;
      });
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Failed to approve request: ' + err.message);
    } finally {
      setProcessingApproval(prev => {
        const newState = { ...prev };
        delete newState[txId];
        return newState;
      });
    }
  };

  const handleDenyRequest = async (txId, requesterName) => {
    if (!window.confirm(`Are you sure you want to deny this request from ${requesterName}? This will delete the coin and cannot be undone.`)) {
      return;
    }

    setProcessingApproval(prev => ({ ...prev, [txId]: 'denying' }));

    try {
      const idToken = auth.user.id_token;
      await apiService.denyCoinRequest(idToken, txId);

      // Refresh pending requests
      const pendingData = await apiService.getPendingCoinRequests(idToken, orgId);
      setPendingRequests(pendingData.transactions || []);

      // Clear editing state
      setEditingMessage(prev => {
        const newState = { ...prev };
        delete newState[txId];
        return newState;
      });
    } catch (err) {
      console.error('Error denying request:', err);
      alert('Failed to deny request: ' + err.message);
    } finally {
      setProcessingApproval(prev => {
        const newState = { ...prev };
        delete newState[txId];
        return newState;
      });
    }
  };

  const handleInviteSent = async () => {
    // Refresh members list after invite is sent
    try {
      const idToken = auth.user.id_token;
      const membersData = await apiService.getOrganizationMembers(idToken, orgId);
      setMembers(membersData.members || []);
    } catch (err) {
      console.error('Error refreshing members:', err);
    }
  };

  const handleRemoveMember = async (member) => {
    const currentUserId = auth.user.profile.sub;

    // If removing yourself, use the leave organization flow instead
    if (member.user_id === currentUserId) {
      handleLeaveOrganization();
      return;
    }

    // Confirm removal
    const confirmMessage = member.is_admin === 1
      ? `Are you sure you want to remove ${member.display_name} (Admin) from this organization?`
      : `Are you sure you want to remove ${member.display_name} from this organization?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const idToken = auth.user.id_token;
      await apiService.removeOrganizationMember(idToken, orgId, member.user_id);

      // Refresh members list
      const membersData = await apiService.getOrganizationMembers(idToken, orgId);
      setMembers(membersData.members || []);
    } catch (err) {
      console.error('Error removing member:', err);
      alert('Failed to remove member: ' + err.message);
    }
  };

  const handleToggleAdminPrivilege = async (member) => {
    const currentUserId = auth.user.profile.sub;

    // Prevent modifying your own privileges
    if (member.user_id === currentUserId) {
      alert('You cannot modify your own privileges.');
      return;
    }

    const newIsAdmin = member.is_admin === 1 ? false : true;
    const confirmMessage = newIsAdmin
      ? `Are you sure you want to grant admin privileges to ${member.display_name}?`
      : `Are you sure you want to revoke admin privileges from ${member.display_name}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const idToken = auth.user.id_token;
      await apiService.updateOrganizationMemberPrivilege(idToken, orgId, member.user_id, newIsAdmin);

      // Refresh members list
      const membersData = await apiService.getOrganizationMembers(idToken, orgId);
      setMembers(membersData.members || []);
    } catch (err) {
      console.error('Error updating member privilege:', err);
      alert('Failed to update member privilege: ' + err.message);
    }
  };

  // Callback to refresh transactions after sending a coin
  const handleCoinSent = async () => {
    try {
      const idToken = auth.user.id_token;
      const transactionsData = await apiService.getUserTransactionsById(idToken, orgId, {
        type: 'sent',
        limit: transactionsToShow
      });
      setTransactions(transactionsData.transactions || []);
    } catch (err) {
      console.error('Error refreshing transactions:', err);
    }
  };

  const handleLeaveOrganization = async () => {
    const currentUserId = auth.user.profile.sub;

    // Check if user is the last admin (exclude the org entity itself)
    const adminCount = members.filter(m => m.is_admin === 1 && m.user_id !== organization.user_id).length;

    if (isAdmin && adminCount === 1) {
      alert('You are the last admin of this organization. Please promote another member to admin before leaving, or have another admin remove you.');
      return;
    }

    // Confirm leaving
    const confirmMessage = isAdmin
      ? `Are you sure you want to leave ${organization.display_name}? You are an admin - consider transferring your responsibilities first.`
      : `Are you sure you want to leave ${organization.display_name}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const idToken = auth.user.id_token;
      await apiService.removeOrganizationMember(idToken, orgId, currentUserId);

      // Redirect to my organizations page
      navigate('/my-organizations');
    } catch (err) {
      console.error('Error leaving organization:', err);
      alert('Failed to leave organization: ' + err.message);
    }
  };

  // Sort members: organization entity first, then admins, then members (each group sorted by last name)
  const sortedMembers = useMemo(() => {
    if (!organization) return members;
    return [...members].sort((a, b) => {
      // Organization entity always comes first
      if (a.user_id === organization.user_id) return -1;
      if (b.user_id === organization.user_id) return 1;

      // Convert is_admin to boolean for comparison
      const aIsAdmin = !!a.is_admin;
      const bIsAdmin = !!b.is_admin;

      // Admins come before non-admins
      if (aIsAdmin && !bIsAdmin) return -1;
      if (!aIsAdmin && bIsAdmin) return 1;

      // Within same group, sort by last name (or full name if no last name)
      const getLastName = (displayName) => {
        const parts = (displayName || '').trim().split(/\s+/);
        return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : displayName.toLowerCase();
      };

      const lastNameA = getLastName(a.display_name);
      const lastNameB = getLastName(b.display_name);

      return lastNameA.localeCompare(lastNameB);
    });
  }, [members, organization]);

  if (loading) {
    return (
      <div className="org-detail-container">
        <div className="loading-container">
          <p>Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-detail-container">
        <div className="error-container">
          <h2>Error Loading Organization</h2>
          <p>{error}</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="org-detail-container">
        <div className="error-container">
          <h2>Organization Not Found</h2>
          <p>The organization you're looking for doesn't exist.</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="org-detail-container">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>You must be a member of this organization to view this page.</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  const displayedMembers = sortedMembers.slice(0, 3);

  return (
    <div className="org-detail-container">
      {currentUser?.user_type !== 'organization' && (
        <BackButton />
      )}

      {/* Organization Header */}
      <section className="org-header">
        <div className="org-avatar-large">
          {organization.display_name?.charAt(0)?.toUpperCase() || 'O'}
        </div>
        <div className="org-info">
          <h1 className="org-name">{organization.display_name}</h1>
          <p className="org-email">{organization.email}</p>
          <div className="org-badges">
            <span className="org-type-badge">Organization</span>
            {isAdmin && <span className="admin-badge">Admin</span>}
          </div>
        </div>
        {organization && auth.user?.profile?.sub !== organization.user_id && (
          <button
            className="leave-org-button"
            onClick={handleLeaveOrganization}
            title="Leave this organization"
          >
            Leave Organization
          </button>
        )}
      </section>

      {/* Pending Coin Requests (Admin Only, except for organization user_type) */}
      {isAdmin && currentUser?.user_type !== 'organization' && (
        <section className="org-section pending-requests-section">
          <h2>Pending Coin Requests ({pendingRequests.length})</h2>
          {pendingRequests.length === 0 ? (
            <div className="empty-state-small">
              <p>No pending coin requests</p>
            </div>
          ) : (
            <>
              <p className="section-description">
                Users have requested coins from {organization.display_name}. You can approve or deny each request, and optionally edit the message before approving.
              </p>
              <div className="requests-list">
                {pendingRequests.map(request => {
                  const currentMessage = editingMessage[request.tx_id] !== undefined
                    ? editingMessage[request.tx_id]
                    : request.description;
                  const isProcessing = processingApproval[request.tx_id];

                  return (
                    <div key={request.tx_id} className="request-item">
                      <div className="request-sender">
                        <div className="sender-avatar">
                          {request.receiver_name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="sender-info">
                          <div className="sender-name">{request.receiver_name}</div>
                          <div className="request-timestamp">{formatTimestamp(request.created_at)}</div>
                        </div>
                      </div>

                      <div className="request-message-edit">
                        <label htmlFor={`org-message-${request.tx_id}`}>
                          Message (you can edit this before approving):
                        </label>
                        <RichTextEditor
                          value={currentMessage}
                          onChange={(newMessage) => handleMessageEdit(request.tx_id, newMessage)}
                          disabled={isProcessing}
                          maxLength={10000}
                          placeholder="Edit the message..."
                        />
                      </div>

                      <div className="request-actions">
                        <button
                          onClick={() => handleApproveRequest(request.tx_id, request.description)}
                          className="approve-button"
                          disabled={isProcessing}
                        >
                          {isProcessing === 'approving' ? '✓ Approving...' : '✓ Approve'}
                        </button>
                        <button
                          onClick={() => handleDenyRequest(request.tx_id, request.receiver_name)}
                          className="deny-button"
                          disabled={isProcessing}
                        >
                          {isProcessing === 'denying' ? '✗ Denying...' : '✗ Deny'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {/* Send Organizational Coin (Admin Only, except for organization user_type) */}
      {isAdmin && currentUser?.user_type !== 'organization' && (
        <SendOrganizationalCoin
          organization={organization}
          onSuccess={handleCoinSent}
        />
      )}

      {/* Members Card */}
      <section className="org-section members-section">
        <div className="section-header">
          <h2>Members <span className="member-count">{members.length}</span></h2>
          {isAdmin && (
            <button className="invite-button" onClick={() => setShowInviteModal(true)}>
              Invite Members
            </button>
          )}
        </div>
        {members.length === 0 ? (
          <div className="empty-state-small">
            <p>No members yet</p>
          </div>
        ) : (
          <>
            <div className="members-grid">
              {displayedMembers.map(member => (
                <div
                  key={member.user_id}
                  className={`member-card ${organization && member.user_id === organization.user_id ? 'org-entity-card' : ''}`}
                >
                  <div
                    className="member-card-content"
                    onClick={() => navigate(`/profile/${member.user_id}`)}
                  >
                    <div className="member-avatar">
                      {member.display_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="member-info">
                      <div className="member-name">{member.display_name}</div>
                      <div className="member-badges">
                        {organization && member.user_id === organization.user_id && <span className="member-org-badge">Organization</span>}
                        {member.is_admin === 1 && <span className="member-admin-badge">Admin</span>}
                      </div>
                    </div>
                  </div>
                  {organization && isAdmin && member.user_id !== organization.user_id && (
                    <div className="member-actions">
                      {member.user_id !== auth.user.profile.sub && (
                        <button
                          className={`toggle-admin-button ${member.is_admin === 1 ? 'revoke' : 'grant'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAdminPrivilege(member);
                          }}
                          title={member.is_admin === 1 ? 'Revoke admin privileges' : 'Grant admin privileges'}
                        >
                          {member.is_admin === 1 ? '👤' : '⚡'}
                        </button>
                      )}
                      <button
                        className="remove-member-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveMember(member);
                        }}
                        title="Remove member"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {members.length > 3 && (
              <button
                onClick={() => setShowAllMembersModal(true)}
                className="show-all-button"
              >
                View All
              </button>
            )}
          </>
        )}
      </section>

      {/* Recent Transactions */}
      <section className="org-section transactions-section">
        <div className="section-header">
          <h2>Recent Transactions</h2>
          <div className="coins-controls">
            <label htmlFor="transactions-limit">Show:</label>
            <select
              id="transactions-limit"
              value={transactionsToShow}
              onChange={(e) => setTransactionsToShow(Number(e.target.value))}
              className="coins-select"
            >
              <option value={3}>Last 3</option>
              <option value={5}>Last 5</option>
              <option value={7}>Last 7</option>
              <option value={10}>Last 10</option>
            </select>
          </div>
        </div>
        {transactions.filter(tx => !tx.pending).length === 0 ? (
          <div className="empty-state-small">
            <p>No transactions yet</p>
          </div>
        ) : (
          <div className="coins-list">
            {transactions.filter(tx => !tx.pending).map(tx => (
              <CoinItem
                key={tx.tx_id}
                id={tx.tx_id}
                coinType={tx.coin_type}
                senderName={tx.receiver_name}
                senderId={tx.receiver_id}
                message={tx.description || 'No description'}
                timestamp={formatTimestamp(tx.created_at)}
                senderEmail={tx.receiver_email}
                direction="sent"
              />
            ))}
          </div>
        )}
      </section>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          organizationId={orgId}
          organizationName={organization.display_name}
          onClose={() => setShowInviteModal(false)}
          onInviteSent={handleInviteSent}
        />
      )}

      {/* View All Members Modal */}
      {showAllMembersModal && (
        <div className="modal-overlay" onClick={() => setShowAllMembersModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>All Members ({members.length})</h2>
              <button className="modal-close-button" onClick={() => setShowAllMembersModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="members-list-scrollable">
                {sortedMembers.map(member => (
                  <div
                    key={member.user_id}
                    className={`member-card ${organization && member.user_id === organization.user_id ? 'org-entity-card' : ''}`}
                  >
                    <div
                      className="member-card-content"
                      onClick={() => {
                        setShowAllMembersModal(false);
                        navigate(`/profile/${member.user_id}`);
                      }}
                    >
                      <div className="member-avatar">
                        {member.display_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="member-info">
                        <div className="member-name">{member.display_name}</div>
                        <div className="member-badges">
                          {organization && member.user_id === organization.user_id && <span className="member-org-badge">Organization</span>}
                          {member.is_admin === 1 && <span className="member-admin-badge">Admin</span>}
                        </div>
                      </div>
                    </div>
                    {organization && isAdmin && member.user_id !== organization.user_id && (
                      <div className="member-actions">
                        {member.user_id !== auth.user.profile.sub && (
                          <button
                            className={`toggle-admin-button ${member.is_admin === 1 ? 'revoke' : 'grant'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleAdminPrivilege(member);
                            }}
                            title={member.is_admin === 1 ? 'Revoke admin privileges' : 'Grant admin privileges'}
                          >
                            {member.is_admin === 1 ? '👤' : '⚡'}
                          </button>
                        )}
                        <button
                          className="remove-member-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveMember(member);
                          }}
                          title="Remove member"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationDetail;
